import React, { useState } from 'react';
import CustomSingleSelect from '../../../../util_components/custom_single_select';
import { cellHalf, recordingLine } from './formatters';
import './prices_table.css';

const TERMS = [
  { key: 'm1', label: '1 month' },
  { key: 'm3', label: '3 months' },
  { key: 'm6', label: '6 months' },
  { key: 'm13', label: '13 months' }
];

function Half({ symbol, total, perMonth, recording }) {
  const parts = cellHalf(symbol, total, perMonth);
  const rec = recordingLine(symbol, recording);
  return (
    <td className="pt-cell">
      <div className="pt-total">{parts.total}</div>
      {parts.perMonth ? <div className="pt-permonth">{parts.perMonth}</div> : null}
      {rec ? <div className="pt-recording">{rec}</div> : null}
    </td>
  );
}

function Cell({ symbol, cell, showLto }) {
  if (!cell) {
    return (
      <>
        {showLto ? <td className="pt-cell">—</td> : null}
        <td className="pt-cell">—</td>
      </>
    );
  }
  return (
    <>
      {showLto ? (
        <Half symbol={symbol} total={cell.lto_total} perMonth={cell.lto_per_month} />
      ) : null}
      <Half
        symbol={symbol}
        total={cell.post_total}
        perMonth={cell.post_per_month}
        recording={cell.recording_access}
      />
    </>
  );
}

export default function PricesTable(props) {
  const {
    data,
    loading,
    error,
    currency,
    currencyOptions,
    onCurrencyChange,
    isSuperUser,
    onRefreshCache
  } = props;
  const symbol = data?.currency_symbol || '';
  const [showAll, setShowAll] = useState(false);
  const [showLto, setShowLto] = useState(false);

  const allRows = Array.isArray(data?.rows) ? data.rows : [];
  // Default view caps at 7 sessions/week (group-class rows are < 1); the toggle reveals the hidden 8-21 tiers.
  const hasHighVolume = allRows.some((r) => Number(r.sessions_per_week) > 7);
  const visibleRows = showAll ? allRows : allRows.filter((r) => Number(r.sessions_per_week) <= 7);

  return (
    <div className="pt-wrap">
      <div className="pt-toolbar">
        <CustomSingleSelect
          label="Currency"
          name="prices_table_currency"
          value={currency}
          onChange={onCurrencyChange}
          options={currencyOptions || []}
          defaultOption="Select currency"
        />
        {hasHighVolume ? (
          <button type="button" className="pt-toggle" onClick={() => setShowAll((s) => !s)}>
            {showAll ? 'Hide 8+ sessions/week packages' : 'Show 8+ sessions/week packages'}
          </button>
        ) : null}
        <label className="pt-checkbox">
          <input type="checkbox" checked={showLto} onChange={(e) => setShowLto(e.target.checked)} />
          Show LTO prices
        </label>
        {isSuperUser ? (
          <button
            type="button"
            className="pt-toggle"
            disabled={loading}
            onClick={onRefreshCache}
            title="Rebuild the cached prices for this currency from the database"
          >
            Refresh Cache
          </button>
        ) : null}
      </div>

      <div className="pt-legend">
        These are the logged-in offer prices shown to users.{' '}
        {showLto ? (
          <>
            <b>LTO</b> = limited-time first-cycle offer; <b>Post-LTO</b> = standard recurring price.
            Consistency plans never include an LTO.{' '}
          </>
        ) : (
          <>Showing standard recurring (post-LTO) prices. </>
        )}
        Per-month figures are per 4-week cycle. Orange lines are what a paid recording grant adds to
        that term; an extra session beyond the plan is never charged for recordings.
      </div>

      {loading ? <div className="pt-status">Loading prices…</div> : null}
      {error ? <div className="pt-status pt-error">{error}</div> : null}

      {!loading && !error && data && (!Array.isArray(data.rows) || data.rows.length === 0) ? (
        <div className="pt-status">No prices available for this currency.</div>
      ) : null}

      {!loading && !error && data && Array.isArray(data.rows) && data.rows.length > 0 ? (
        <div className="pt-scroll">
          <table className="pt-table">
            <thead>
              <tr>
                <th className="pt-plan-col" rowSpan={showLto ? 2 : 1} scope="col">
                  Plan
                </th>
                {TERMS.map((t) => (
                  <th
                    key={t.key}
                    colSpan={showLto ? 2 : 1}
                    className="pt-term-head"
                    scope={showLto ? 'colgroup' : 'col'}
                  >
                    {t.label}
                  </th>
                ))}
              </tr>
              {showLto ? (
                <tr>
                  {TERMS.map((t) => (
                    <React.Fragment key={t.key}>
                      <th
                        className="pt-sub-head"
                        scope="col"
                        aria-label={`${t.label} limited-time offer`}
                      >
                        LTO
                      </th>
                      <th
                        className="pt-sub-head"
                        scope="col"
                        aria-label={`${t.label} regular price`}
                      >
                        Post-LTO
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              ) : null}
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={`${row.plan_type}-${row.sessions_per_week}`}>
                  <td className="pt-plan-col pt-plan-label" scope="row">
                    {row.plan_label}
                    {TERMS.some((t) => row.cells?.[t.key]?.recording_access) ? (
                      <div className="pt-recording">+ for recording access</div>
                    ) : null}
                  </td>
                  {TERMS.map((t) => (
                    <Cell key={t.key} symbol={symbol} cell={row.cells?.[t.key]} showLto={showLto} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
